import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  CreateProductDto,
  GetProductQueryDto,
  GetProductsResponseDto,
  ProductDto,
  UpdateProductDto,
} from "@/types/api/api-types";

//#region Get

const getAllProducts = async (query?: GetProductQueryDto) => {
  const response = await axiosApi.get<GetProductsResponseDto>("v1/product", {
    params: query,
  });
  return response.data;
};

export const useGetAllProducts = (query?: GetProductQueryDto) => {
  return useQuery({
    queryKey: ["product", "all", query],
    queryFn: () => getAllProducts(query),
  });
};

//#endregion

//#region Post

const createProduct = async (payload: CreateProductDto) => {
  return axiosApi.post<ProductDto>("v1/product", payload);
};

export const useCreateProduct = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", "all"] });
      toast.success(t("productApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("productApi.createError"));
    },
  });
};

//#endregion

//#region Patch

const updateProduct = async ({ id, payload }: { id: string; payload: UpdateProductDto }) => {
  return axiosApi.patch<ProductDto>(`v1/product/${id}`, payload);
};

export const useUpdateProduct = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", "all"] });
      toast.success(t("productApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("productApi.updateError"));
    },
  });
};

//#endregion

//#region Delete

const deleteProduct = async (id: string) => {
  return axiosApi.delete(`v1/product/${id}`);
};

export const useDeleteProduct = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", "all"] });
      toast.success(t("productApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("productApi.deleteError"));
    },
  });
};

//#endregion
